/**
 * Pattern Manager for saving and loading guitar neck patterns with advanced features
 */
import { getUserItems, getUserItem, saveUserItem, deleteUserItem } from '../data/userStore';

// Default demo preset - C Phrygian Dominant with Phrygian Cadence
export const DEFAULT_DEMO_PRESET = {
  id: 'demo-preset',
  title: 'Demo - C Phrygian Dominant',
  rootNote: 'C',
  currentMode: 'Phrygian Dominant',
  currentProgression: 'Phrygian Cadence (i-bII)',
  selectedNotes: [],
  highlightTriads: false,
  showConnectionLines: false,
  timestamp: 1705500000000, // Fixed timestamp for consistency
  favorite: true,
  isDefault: true
};

/**
 * Get all patterns from storage
 * @returns {Array} Array of pattern objects
 */
export const getAllPatterns = () => {
  try {
    // Read from the user mirror and sort by timestamp (newest first)
    return getUserItems('pattern').sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error getting patterns:', error);
    return [];
  }
};

/**
 * Save a pattern to local storage
 * @param {Object} patternData - Pattern data to save
 * @returns {string|null} - Pattern ID if saved successfully, null otherwise
 */
export const savePattern = (patternData) => {
  try {
    // Generate a unique ID for new patterns if not provided
    const patternId = patternData.id || `pattern_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add or update pattern
    const finalPattern = {
      ...patternData,
      id: patternId,
      timestamp: Date.now(),
      favorite: patternData.favorite || false
    };

    // Save to the user mirror (write-through IndexedDB)
    saveUserItem('pattern', patternId, finalPattern);
    return patternId;
  } catch (error) {
    return null;
  }
};

/**
 * Load a pattern from storage by ID
 * @param {string} patternId - ID of the pattern to load
 * @returns {Object|null} - Pattern object if found, null otherwise
 */
export const loadPattern = (patternId) => {
  try {
    return getUserItem('pattern', patternId) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Delete a pattern from storage
 * @param {string} patternId - ID of the pattern to delete
 * @returns {boolean} - Success status
 */
export const deletePattern = (patternId) => {
  try {
    if (getUserItem('pattern', patternId)) {
      deleteUserItem('pattern', patternId);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error deleting pattern:', error);
    return false;
  }
};

/**
 * Toggle favorite status of a pattern
 * @param {string} patternId - ID of the pattern to toggle
 * @returns {boolean} - Success status
 */
export const toggleFavorite = (patternId) => {
  try {
    const existing = getUserItem('pattern', patternId);

    if (existing) {
      const updated = { ...existing, favorite: !existing.favorite };
      saveUserItem('pattern', patternId, updated);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error toggling favorite status:', error);
    return false;
  }
};

/**
 * Search for patterns based on title or scale
 * @param {Array} patterns - Array of pattern objects
 * @param {string} searchTerm - Search term
 * @returns {Array} - Filtered array of pattern objects
 */
export const searchPatterns = (patterns, searchTerm) => {
  if (!searchTerm.trim()) {
    return patterns;
  }
  
  const lowerCaseSearch = searchTerm.toLowerCase();
  return patterns.filter(pattern => 
    pattern.title.toLowerCase().includes(lowerCaseSearch) ||
    `${pattern.rootNote} ${pattern.currentMode}`.toLowerCase().includes(lowerCaseSearch)
  );
};
